import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import NavHeader from '../shared/Nav-Header';
import styles from './settings.module.css';

const GuestSettings = () => {
    const [emailOrPhone, setEmailOrPhone] = useState('');
    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [deletePassword, setDeletePassword] = useState('');
    const [error, setError] = useState('');
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const token = useMemo(() => localStorage.getItem('token'), []);

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                if (!token) {
                    setIsLoggedIn(false);
                    setLoading(false);
                    return;
                }

                const response = await axios.get('/api/authApplicant/get-name-email', {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setEmailOrPhone(response.data.emailOrPhone);
                setName(response.data.name);
                setIsLoggedIn(true);
            } catch (error) {
                console.error('Error fetching user data:', error);
                const errorMessage2 = error.response?.data?.message || 'An error occurred';
                setError(errorMessage2);
                setIsLoggedIn(false);
            } finally {
                setLoading(false);
            }
        };

        fetchUserData();
    }, [navigate]);

    const handleLogin = () => {
        navigate('/login');
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        navigate('/');
    };

    const handleDeleteProfile = async () => {
        if (deletePassword) {
            try {
                const response = await axios.post('/api/authApplicant/delete-profile', {
                    password: deletePassword
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                localStorage.removeItem('token');
                navigate('/');
            } catch (error) {
                console.error('Error deleting profile:', error);
            }
        }
    };

    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
    };

    const handleDeletePasswordChange = (e) => {
        setDeletePassword(e.target.value);
    };

    if (loading) {
        return (
            <div>
                <NavHeader />
                <div className={styles.settingsContainer}>
                    <div className={styles.loadingContainer}>
                        <div className={styles.loadingSpinner}></div>
                        <p>Loading your settings...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <NavHeader />
            <div className={styles.settingsContainer}>
                {!isLoggedIn && (
                    <div className={styles.loginOverlay}>
                        <div className={styles.loginPrompt}>
                            <h3>Login Required</h3>
                            <p>Please log in to access your settings and manage your account.</p>
                            <button 
                                className={styles.loginButton}
                                onClick={handleLogin}
                            >
                                Login
                            </button>
                        </div>
                    </div>
                )}
                
                <div className={`${styles.settingsContent} ${!isLoggedIn ? styles.blurred : ''}`}>
                    <div className={styles.header}>
                        <div className={styles.avatar}>
                            <div className={styles.avatarIcon}>👤</div>
                        </div>
                        <h2 className={styles.title}>Settings</h2>
                        <div className={styles.statusBadge}>
                            <span className={styles.statusDot}></span>
                            You are currently logged in
                        </div>
                    </div>

                    <div className={styles.settingsCard}>
                        <div className={styles.settingsInfo}>
                            <div className={styles.infoItem}>
                                <span className={styles.label}>📧 Email:</span>
                                <span className={styles.value}>{emailOrPhone}</span>
                            </div>
                            <div className={styles.infoItem}>
                                <span className={styles.label}>👤 Name:</span>
                                <span className={styles.value}>{name}</span>
                            </div>
                        </div>

                        <div className={styles.actionSection}>
                            <div className={styles.passwordChange}>
                                <label htmlFor="password" className={styles.inputLabel}>
                                    🔒 Change Password:
                                </label>
                                <input 
                                    type="password" 
                                    id="password" 
                                    value={password} 
                                    onChange={handlePasswordChange}
                                    className={styles.passwordInput}
                                    placeholder="Enter new password"
                                />
                            </div>

                            <div className={styles.deleteProfile}>
                                <label htmlFor="delete-password" className={styles.inputLabel}>
                                    🗑️ Enter Password to Delete Profile:
                                </label>
                                <input 
                                    type="password" 
                                    id="delete-password" 
                                    value={deletePassword} 
                                    onChange={handleDeletePasswordChange}
                                    className={styles.passwordInput}
                                    placeholder="Confirm with password"
                                />
                            </div>
                        </div>

                        <div className={styles.buttonGroup}>
                            <button className={styles.logoutButton} onClick={handleLogout}>
                                <span className={styles.buttonIcon}>🚪</span>
                                Logout
                            </button>
                            <button className={styles.deleteButton} onClick={handleDeleteProfile}>
                                <span className={styles.buttonIcon}>🗑️</span>
                                Delete Profile
                            </button>
                        </div>

                        <div className={styles.warning}>
                            ⚠️ Profile deletion is permanent and cannot be undone
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GuestSettings;